import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import {
  pickRole20Schema,
  Role20Schema,
  Role20SchemaControl,
} from './role20-notes-corporate-information.rules';

/**
 * Pure validator for Role 20 (Notes - Corporate Information).
 *
 * Mirrors the editor's role20-710000-notes-corporate-information.component.ts
 * validation surface:
 *
 *  - Static required fields from schema (control.required === true OR
 *    validators[{ name: 'required' }]) — see the component's getValidators()
 *    which pushes Validators.required for either source.
 *  - Cross-sheet conditional-required fields described by
 *    control.requiredIfCrossSheet: when the referenced sheet's field equals
 *    the configured value, the control becomes required; otherwise the
 *    component clears its validators entirely (setupCrossSheetValidation).
 *  - Integer-min-0 rule for NonNegativeIntegerItemType controls. The current
 *    role 20 schemas don't declare any such controls but the component
 *    applies the rule schema-driven, so the validator mirrors it for
 *    parity safety if a future schema revision adds one.
 *
 * ---- Schema variant handling ----------------------------------------------
 *
 * Role 20 has TWO form JSONs (role-710000-mfrs.form.json and
 * role-710000-mpers.form.json) that differ only in qname prefix. The
 * component fetches the right one at mount time based on
 * filingPrerequisites['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'].
 *
 * The pure path gets the same value through context.accountingStandard
 * (assembled by ConversionValidatorService.buildContext from filingInfo —
 * which is the same DEI field). No change to the SheetValidator interface
 * is required; the existing context field already carries enough
 * information. Selection is delegated to pickRole20Schema so the component
 * and validator stay DRY.
 */

interface SchemaWithControls {
  controls: ReadonlyArray<Role20SchemaControl>;
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Mirrors the component's getValidators() check: a control is statically
 * required when `required === true` OR when its validators array contains
 * an entry with `name === 'required'`. Either source produces the same
 * Validators.required push so we treat them identically.
 */
function isStaticallyRequired(control: Role20SchemaControl): boolean {
  if (control.required === true) return true;
  return Array.isArray(control.validators)
    ? control.validators.some((validator) => validator?.name === 'required')
    : false;
}

function checkStaticRequiredErrors(
  schema: SchemaWithControls,
  sheetData: Record<string, any>
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const control of schema.controls) {
    if (isStaticallyRequired(control) && isEmpty(sheetData[control.qname])) {
      errors[control.qname] = `${control.qname} is required`;
    }
  }
  return errors;
}

/**
 * Reads the referenced sheet's field value out of context.crossSheetData.
 * When the orchestrator didn't include the referenced sheet in the bundle
 * (e.g. the dependency map missed it) we return undefined, which mirrors
 * the component's behaviour when scopeOfFiling has not been loaded yet:
 * shouldBeRequired resolves to false and the field is not flagged.
 */
function readCrossSheetFieldValue(
  context: SheetValidationContext,
  sheet: string,
  field: string
): unknown {
  const bundle = context.crossSheetData;
  if (!bundle) return undefined;
  const sheetPayload = bundle.get(sheet as Parameters<typeof bundle.get>[0]);
  if (!sheetPayload) return undefined;
  return sheetPayload[field];
}

function checkCrossSheetRequiredErrors(
  schema: SchemaWithControls,
  sheetData: Record<string, any>,
  context: SheetValidationContext
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const control of schema.controls) {
    const rule = control.requiredIfCrossSheet;
    if (!rule) continue;

    const refValue = readCrossSheetFieldValue(context, rule.sheet, rule.field);
    const shouldBeRequired = refValue === rule.equals;
    if (shouldBeRequired && isEmpty(sheetData[control.qname])) {
      errors[control.qname] = `${control.qname} is required`;
    }
  }
  return errors;
}

export const role20NotesCorporateInformationValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.notesCorporateInformation,
  // Tier 1 by primary characteristic (required-field driven). The
  // cross-sheet requiredIfCrossSheet rule references scopeOfFiling, but it
  // is a pure equality check against a single field — no calculations, no
  // row repetition — so it stays in Tier 1. Orchestrator must include
  // scopeOfFiling in the cross-sheet bundle; without it the rule silently
  // degrades to "not required" (matches the component's pre-subscription
  // state).
  tier: 1,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const schema = pickRole20Schema(context.accountingStandard) as unknown as SchemaWithControls & Role20Schema;

    const errors: Record<string, string> = {
      ...checkStaticRequiredErrors(schema, sheetData),
      ...checkCrossSheetRequiredErrors(schema, sheetData, context),
    };

    return {
      hasErrors: Object.keys(errors).length > 0,
      hasSuggestedChanges: false,
      errors,
    };
  },
};
